import { effect, inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, finalize, map, Observable, of, throwError } from 'rxjs';
import { ApiConstants } from '../../common/constants/api-url.constant';
import { MessageService } from 'primeng/api';
import { OrderDetails, PaymentDetails, PaymentPlanId } from '../../common/models/order.model';
import { CheckoutSignalsService } from './checkout-signals.service';
import { SharedSignalsService } from '../../common/services/shared-signals.service';
import { LocalStorageService } from '../../common/services/local-storage.service';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../common/services/auth.service';
import { SessionVariable } from '../../common/enum/session-variable';
import { TranslationService } from '../../common/services/translation.service';
import { getDefaultPaymentPlanId, sortPaymentPlans } from '../../common/utils/payment-plan.util';
import {
  Card,
  PayLaterRequest,
  PayLaterResponse,
  CreateSessionRequest,
  // TODO: Uncomment to enable MyFatoorah
  // CreateSessionResponse,
  MpgsCheckoutSessionResponse,
  MpgsSessionFailureResponse,
  PaymentPlansResponse,
  SystemAvailabilityResponse,
  ValidateOrderValueResponse,
  // TODO: Uncomment to enable MyFatoorah
  // VerifyCreditCardV2Response,
  AuthenticateCivilIdResponse,
  RequestCivilIdStatusResponse,
  PolicyAcceptanceRequest,
} from '../checkout-model';

@Injectable({
  providedIn: 'root',
})
export class CheckoutService {
  private http = inject(HttpClient);
  private toasterService = inject(MessageService);
  private apiConstants = inject(ApiConstants);

  transfer = inject(CheckoutSignalsService);
  transferSignal = inject(SharedSignalsService);
  public storageService = inject(LocalStorageService);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  authService = inject(AuthService);
  private translationService = inject(TranslationService);

  orderId: string;

  constructor() {
    effect(() => {
      this.orderId = this.transfer?.orderId();
    });
  }

  getPaymentInfo(orderId: string): Observable<OrderDetails | null> {
    this.transfer.setIsLoading(true);

    return this.http.post<OrderDetails>(this.apiConstants.PAYMENT_INFO(orderId), null).pipe(
      map((resp) => {
        this.transfer.setOrderDetails(resp);

        if (resp?.redirectUrl && !resp?.redirectUrl.startsWith('?')) {
          this.transferSignal.setMerchantRedirectUrl(resp.redirectUrl);
        }

        this.storageService.setItem(SessionVariable.PAYMENT_ID, resp.paymentId);
        this.storageService.setItem(SessionVariable.REFERENCE_ID, resp.referenceId);
        this.storageService.setItem(SessionVariable.PAYMENT_DATE, resp.paymentDate);
        this.storageService.setItem(SessionVariable.ORDER_ID_FOR_CNET, resp.id);

        if (resp?.status === 'INCOMPLETED') {
          this.router.navigate([`/secure-checkout/${orderId}/payment/timeout`], {
            relativeTo: this.activatedRoute,
          });
          return null;
        } else if (
          resp?.status !== 'INITIATED' &&
          resp?.status !== 'REJECTED' &&
          resp?.status !== 'CONFIRMED'
        ) {
          this.router.navigate([`/secure-checkout/${orderId}/payment/error`], {
            relativeTo: this.activatedRoute,
          });
          return null;
        }

        return resp;
      }),
      catchError((err) => {
        this.transfer.setIsLoading(false);
        this.showMessage('error', err?.message || err);
        return throwError(() => err);
      }),
      finalize(() => {
        this.transfer.setIsLoading(false);
      })
    );
  }

  getStandalonePaymentInfo(paymentToken: string): Observable<PaymentDetails | null> {
    return this.http
      .get<PaymentDetails>(this.apiConstants.STANDALONE_PAYMENT_INFO(paymentToken))
      .pipe(catchError((err) => throwError(() => err)));
  }

  getInstallments(orderId: string): Observable<PaymentPlansResponse[]> {
    return this.http
      .get<PaymentPlansResponse[]>(this.apiConstants.PAYMENT_PLAN(orderId))
      .pipe(
        map((res) => {
          const resp = sortPaymentPlans(
            res.map((r) => ({
              ...r,
              installment: r.installment.filter((i) => i.amount !== 0),
            }))
          );

          this.transfer.setPaymentOptions(resp);
          this.storageService.setItem(SessionVariable.PAYMENT_OPTIONS, JSON.stringify(resp));

          // set payment plan: keep the customer's previous choice when still offered
          if (resp?.length > 0) {
            const storedPlanId = this.storageService.getItem<PaymentPlanId>(
              SessionVariable.PAYMENT_PLAN_ID
            );
            const isStoredPlanOffered = resp.some((plan) => plan.id === storedPlanId);

            this.transfer.setPaymentPlanId(
              isStoredPlanOffered ? storedPlanId : getDefaultPaymentPlanId(resp)
            );
          }

          return resp; // return processed response
        }),
        catchError((err) => {
          // TODO: Move these checks to CINET validation, as they are not related to payment plans.
          if (err.errorCode === 'T400' || err.errorCode === 'T411') {
            const orderId = this.transfer.resolveOrderId(this.activatedRoute) || this.orderId;

            this.router.navigate([`secure-checkout/${orderId}/rejection-error`], {
              relativeTo: this.activatedRoute,
              state: {
                errorType: 'CNET_REJECTION',
                errorMessage: this.translationService.getLabelValue(
                  'CHECKOUT.REDUCE_CARD_SIZE_MSG'
                ),
                title: err?.message,
              },
            });
          } else {
            this.showMessage('error', err?.message || err);
          }
          return of(null); // return null on error
        })
      );
  }

  getOrderStatus(orderId: string): Observable<any> {
    return this.http.get(this.apiConstants.ORDER_STATUS(orderId));
  }
  getCnetValidationStatus(requestId: string): Observable<any> {
    return this.http.get(this.apiConstants.CNET_AUTHENTICATION_REQUEST(requestId));
  }

  cancelOrder(orderId: string, userId?: any): Observable<any> {
    return this.http.post(this.apiConstants.CANCEL_ORDER_REQUEST(orderId, userId), null);
  }

  cNetValidation(amount: string | number, orderId: any): Observable<any> {
    return this.http.post(this.apiConstants.CNET_SOURCE_VALIDATION(amount, orderId), null);
  }

  createSession(request: CreateSessionRequest): Observable<MpgsCheckoutSessionResponse> {
    const orderId = request.orderId;
    const paymentPlanId = request.paymentPlanId;
    const url = this.apiConstants.CREATE_SESSION(orderId);

    return this.http.post<MpgsCheckoutSessionResponse>(url, { paymentPlanId });
  }

  // TODO: Uncomment to enable MyFatoorah
  // createSession(request: CreateSessionRequest): Observable<CreateSessionResponse> {
  //   const orderId = request.orderId;
  //   const paymentPlanId = request.paymentPlanId;
  //   const url = this.apiConstants.CREATE_SESSION(orderId);

  //   return this.http.post<CreateSessionResponse>(url, { paymentPlanId });
  // }

  resultIndicatorStatus(resultIndicator: string): Observable<Card> {
    const url = this.apiConstants.RESULT_INDICATOR_STATUS(encodeURIComponent(resultIndicator));
    return this.http.post<Card>(url, {});
  }

  // TODO: Uncomment to enable MyFatoorah
  // resultIndicatorStatus(request: any): Observable<any> {
  //   const paymentId = request.paymentId;
  //   const url = this.apiConstants.GET_TRANSACTION_STATUS_V2(paymentId);
  //   return this.http.post(url, request);
  // }
  getMPGSCreditCard(): Observable<Card[]> {
    return this.http.get<Card[]>(this.apiConstants.GET_MPGS_CREDITCARD());
  }
  getMpgsSessionFailure(sessionId: string): Observable<MpgsSessionFailureResponse> {
    return this.http.get<MpgsSessionFailureResponse>(
      this.apiConstants.MPGS_SESSION_FAILURE(encodeURIComponent(sessionId))
    );
  }
  getTransactionStatus(request: any): Observable<any> {
    return this.http.post(this.apiConstants.GET_TRANSACTION_STATUS(), request);
  }
  // TODO: Uncomment to enable MyFatoorah
  // verifyCreditCardV2(sessionId: string): Observable<VerifyCreditCardV2Response> {
  //   return this.http.post<VerifyCreditCardV2Response>(
  //     this.apiConstants.VERIFY_CREDIT_CARD_V2(sessionId),
  //     {},
  //     { responseType: 'text' as 'json' }
  //   );
  // }
  // getTransactionStatusV2(paymentId: string): Observable<VerifyCreditCardV2Response> {
  //   return this.http.post<VerifyCreditCardV2Response>(
  //     this.apiConstants.GET_TRANSACTION_STATUS_V2(paymentId),
  //     {},
  //     { responseType: 'text' as 'json' }
  //   );
  // }
  checkSystemAvailability(): Observable<SystemAvailabilityResponse> {
    return this.http.get<SystemAvailabilityResponse>(this.apiConstants.CHECK_SYSTEM_AVAILABILITY());
  }

  validateOrderValue(orderId: string): Observable<ValidateOrderValueResponse> {
    return this.http.get<ValidateOrderValueResponse>(
      this.apiConstants.VALIDATE_ORDER_VALUE(orderId)
    );
  }
  getTokenInfo(tokenType: 'orderToken' | 'paymentToken', token: string): Observable<any> {
    return this.http.get(this.apiConstants.TOKEN_TYPE(tokenType, token));
  }
  showMessage(severityType: string, toastMessage: string, life: number = 7000) {
    this.toasterService.clear();
    this.toasterService.add({ severity: severityType, detail: toastMessage, life: life });
  }
  initiateCnetRenewal(): Observable<any> {
    return this.http.post(this.apiConstants.CNET_RENEWAL_AUTHENTICATION(), {});
  }

  getCnetRenewalStatus(): Observable<any> {
    return this.http.get(this.apiConstants.GET_CNET_RENEWAL_STATUS());
  }

  authenticateCivilId(civilId: string): Observable<AuthenticateCivilIdResponse> {
    return this.http.post<AuthenticateCivilIdResponse>(
      this.apiConstants.AUTHENTICATE_CIVIL_ID(civilId),
      null
    );
  }

  requestCivilIdStatus(civilId: string): Observable<RequestCivilIdStatusResponse> {
    return this.http.get<RequestCivilIdStatusResponse>(
      this.apiConstants.REQUEST_CIVIL_ID_STATUS(civilId)
    );
  }

  payLater(body: PayLaterRequest): Observable<PayLaterResponse> {
    return this.http.post<PayLaterResponse>(this.apiConstants.PAY_LATER(), body);
  }
  changeLanguage(lang: string): Observable<any> {
    return this.http.put<any>(this.apiConstants.CHANGE_LANGUAGE_PREFERENCE(lang), {});
  }

  policyAcceptance(orderId: string): Observable<PolicyAcceptanceRequest> {
    return this.http.post<PolicyAcceptanceRequest>(
      this.apiConstants.POLICY_ACCEPTANCE(orderId),
      null
    );
  }
}
